import React from 'react';

import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';
import { FormikField } from 'components/FormikField';

import { PropertySelectField } from '../PropertySelectField';

import { getPropertyLabel, getPropertyValue } from './TextTextProperty.helpers';

import styles from './TextTextProperty.module.scss';

type LayerProp = {
  key?: string;
  value?: unknown;
};

type RenderFieldArgs = {
  namePrefix?: string;
  readOnly?: boolean;
  property: LayerProp;
  dependProperty?: LayerProp | null;
  onChanged?: () => void;
};

const _renderField = ({
  namePrefix,
  readOnly,
  property,
  dependProperty,
  onChanged,
}: RenderFieldArgs) => (
  <FormikField
    name={namePrefix ?? ''}
    withControl={false}
    component={PropertySelectField}
    componentProps={{
      type: 'checkbox',
      readOnly,
      property,
      dependProperty,
      renderProperty: _renderContent,
      onChanged,
    }}
  />
);

const _renderContent = ({
  key,
  value,
  dependValue,
}: {
  key: string;
  value?: any;
  dependValue?: any;
}) => {
  return (
    <>
      <Typography
        className={styles.label}
        variant="body2"
        weight="medium"
        mode="compact"
      >
        {getPropertyLabel({
          type: key,
          dependValue,
        })}
      </Typography>
      <Typography
        className={styles.value}
        variant="body2"
        weight="medium"
        mode="compact"
      >
        {getPropertyValue({
          type: key,
          value,
          dependValue,
        })}
      </Typography>
    </>
  );
};

type TextTextPropertyProps = {
  isInitialState?: boolean;
  readOnly?: boolean;
  checkable?: boolean;
  tooltip?: React.ReactNode;
  designType?: string;
  namePrefix?: string;
  property?: LayerProp | null;
  dependProperty?: LayerProp | null;
  onChanged?: () => void;
};

const TextTextProperty = ({
  isInitialState,
  readOnly,
  checkable,
  tooltip,
  designType,
  namePrefix,
  property,
  dependProperty,
  onChanged,
}: TextTextPropertyProps) => {
  if (!property) {
    return null;
  }

  if (isInitialState || (designType === 'PROTOTYPE' && !checkable)) {
    if (tooltip) {
      return (
        <Tooltip
          text={tooltip}
          triggerClassName={styles.fieldTrigger}
          triggerElement={
            <div className={styles.propertyReadOnly}>
              {_renderContent({
                key: property.key,
                value: property.value,
                dependValue: dependProperty ? dependProperty.value : null,
              })}
            </div>
          }
        />
      );
    }

    return (
      <div className={styles.propertyReadOnly}>
        {_renderContent({
          key: property.key,
          value: property.value,
          dependValue: dependProperty ? dependProperty.value : null,
        })}
      </div>
    );
  }

  if (tooltip) {
    return (
      <Tooltip
        text={tooltip}
        triggerClassName={styles.fieldTrigger}
        triggerElement={_renderField({
          namePrefix,
          readOnly,
          property,
          dependProperty,
          onChanged,
        })}
      />
    );
  }

  return _renderField({
    namePrefix,
    readOnly,
    property,
    dependProperty,
    onChanged,
  });
};

export default React.memo(TextTextProperty);
